import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const SociosManagement = () => {
  const { hasPermission } = useAuth();
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showActiveSociosModal, setShowActiveSociosModal] = useState(false);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    cedula: ''
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [formData, setFormData] = useState({
    cedula: '',
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [manageData, setManageData] = useState({
    aportaciones: 0,
    ahorros: 0,
    prestamos: 0
  });

  useEffect(() => {
    if (!hasPermission('socios', 'read')) {
      toast.error('No tienes permisos para ver socios');
      return;
    }
    fetchSocios();
  }, [hasPermission]);

  const fetchSocios = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleManageInputChange = (e) => {
    const { name, value } = e.target;
    setManageData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSocio = (socio) => {
    setSelectedSocio(socio);
    setEditData({
      name: socio.name,
      email: socio.email,
      phone: socio.phone || '',
      address: socio.address || '',
      cedula: socio.cedula
    });
    setShowEditModal(true);
  };

  const handleChangePassword = (socio) => {
    setSelectedSocio(socio);
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handleUpdateSocio = async (e) => {
    e.preventDefault();
    
    if (!hasPermission('socios', 'update')) {
      toast.error('No tienes permisos para actualizar socios');
      return;
    }

    try {
      await axios.put(`/api/users/${selectedSocio._id}`, editData);
      toast.success('Datos del socio actualizados exitosamente');
      setShowEditModal(false);
      fetchSocios();
    } catch (error) {
      console.error('Error updating socio:', error);
      toast.error('Error al actualizar datos del socio');
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!hasPermission('socios', 'update')) {
      toast.error('No tienes permisos para cambiar contraseñas');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.put(`/api/users/${selectedSocio._id}/password`, {
        newPassword: passwordData.newPassword
      });
      toast.success('Contraseña actualizada exitosamente');
      setShowPasswordModal(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Error al actualizar contraseña');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasPermission('socios', 'create')) {
      toast.error('No tienes permisos para crear socios');
      return;
    }

    try {
      // Obtener todos los números de socio existentes
      const numerosExistentes = socios
        .map(s => {
          const match = s.socioInfo?.numeroSocio?.match(/SOC-(\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(n => n > 0);
      
      // Encontrar el siguiente número disponible
      let nextNumber = 1;
      while (numerosExistentes.includes(nextNumber)) {
        nextNumber++;
      }
      
      const numeroSocio = `SOC-${String(nextNumber).padStart(2, '0')}`;
      
      const socioData = {
        ...formData,
        role: 'socio',
        password: formData.cedula, // La cédula será la contraseña por defecto
        socioInfo: {
          numeroSocio: numeroSocio,
          fechaIngreso: new Date(),
          estado: 'activo',
          aportaciones: 0,
          ahorros: 0,
          prestamos: 0
        }
      };

      await axios.post('/api/users', socioData);
      toast.success('Socio creado exitosamente');
      setShowModal(false);
      setFormData({
        cedula: '',
        name: '',
        email: '',
        phone: '',
        address: ''
      });
      fetchSocios();
    } catch (error) {
      console.error('Error creating socio:', error);
      toast.error(error.response?.data?.message || 'Error al crear socio');
    }
  };

  const handleManageSocio = (socio) => {
    setSelectedSocio(socio);
    setManageData({
      aportaciones: socio.socioInfo?.aportaciones || 0,
      ahorros: socio.socioInfo?.ahorros || 0,
      prestamos: socio.socioInfo?.prestamos || 0
    });
    setShowManageModal(true);
  };

  const handleDeleteSocio = (socio) => {
    setSelectedSocio(socio);
    setShowDeleteModal(true);
  };

  const confirmDeleteSocio = async () => {
    if (!hasPermission('socios', 'delete')) {
      toast.error('No tienes permisos para eliminar socios');
      return;
    }

    try {
      setDeleteLoading(true);
      await axios.delete(`/api/users/${selectedSocio._id}`);
      toast.success('Socio eliminado exitosamente');
      setShowDeleteModal(false);
      setSelectedSocio(null);
      fetchSocios();
    } catch (error) {
      console.error('Error deleting socio:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar socio');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpdateSocioData = async (e) => {
    e.preventDefault();
    
    if (!hasPermission('socios', 'update')) {
      toast.error('No tienes permisos para actualizar socios');
      return;
    }

    try {
      await axios.put(`/api/users/${selectedSocio._id}`, {
        socioInfo: {
          ...selectedSocio.socioInfo,
          ...manageData
        }
      });
      
      toast.success('Datos del socio actualizados exitosamente');
      setShowManageModal(false);
      fetchSocios();
    } catch (error) {
      console.error('Error updating socio:', error);
      toast.error('Error al actualizar datos del socio');
    }
  };

  const toggleSocioStatus = async (socioId, currentStatus) => {
    if (!hasPermission('socios', 'update')) {
      toast.error('No tienes permisos para cambiar el estado de socios');
      return;
    }

    try {
      await axios.patch(`/api/users/${socioId}/toggle-status`);
      toast.success(`Socio ${currentStatus ? 'desactivado' : 'activado'} exitosamente`);
      fetchSocios();
    } catch (error) {
      console.error('Error toggling socio status:', error);
      toast.error('Error al cambiar estado del socio');
    }
  };

  const handleLoginAsSocio = async (socio) => {
    try {
      const response = await axios.post('/api/auth/login-as-socio', {
        socioId: socio._id
      });

      if (response.data.success) {
        // Guardar el token en localStorage
        localStorage.setItem('token', response.data.token);
        
        // Guardar datos del usuario
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast.success(`Iniciaste sesión como ${socio.name}`);
        
        // Recargar la página después de un momento para que se sincronice
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
      }
    } catch (error) {
      console.error('Error al hacer login como socio:', error);
      toast.error(error.response?.data?.message || 'Error al iniciar sesión como socio');
    }
  };

  if (!hasPermission('socios', 'read')) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No tienes permisos para acceder a esta página.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2>
              <i className="fas fa-user-friends me-2"></i>
              Gestión de Socios
            </h2>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-success" 
                onClick={() => setShowActiveSociosModal(true)}
              >
                <i className="fas fa-users me-2"></i>
                Ver Socios Activos
              </Button>
              {hasPermission('socios', 'create') && (
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <i className="fas fa-plus me-2"></i>
                  Nuevo Socio
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Tarjetas informativas */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stats-card border-primary">
            <Card.Body>
              <div className="stats-icon text-primary">
                <i className="fas fa-users"></i>
              </div>
              <div className="stats-number text-primary">{socios.length}</div>
              <div className="stats-label">Total Socios</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card border-success">
            <Card.Body>
              <div className="stats-icon text-success">
                <i className="fas fa-user-check"></i>
              </div>
              <div className="stats-number text-success">
                {socios.filter(s => s.isActive).length}
              </div>
              <div className="stats-label">Socios Activos</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card border-warning">
            <Card.Body>
              <div className="stats-icon text-warning">
                <i className="fas fa-user-times"></i>
              </div>
              <div className="stats-number text-warning">
                {socios.filter(s => !s.isActive).length}
              </div>
              <div className="stats-label">Socios Inactivos</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card border-info">
            <Card.Body>
              <div className="stats-icon text-info">
                <i className="fas fa-calendar-plus"></i>
              </div>
              <div className="stats-number text-info">
                {socios.filter(s => {
                  const fechaIngreso = new Date(s.socioInfo?.fechaIngreso);
                  const hoy = new Date();
                  const treintaDiasAtras = new Date(hoy.setDate(hoy.getDate() - 30));
                  return fechaIngreso >= treintaDiasAtras;
                }).length}
              </div>
              <div className="stats-label">Nuevos (30 días)</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-table me-2"></i>
                Lista de Socios ({socios.length})
              </h5>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Socio</th>
                    <th>Cédula</th>
                    <th>Contacto</th>
                    <th>Número Socio</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {socios.map((socio, index) => (
                    <tr key={socio._id}>
                      <td>
                        <strong className="text-primary">{index + 1}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                          <div>
                            <div className="fw-bold">{socio.name}</div>
                            <small className="text-muted">
                              Ingreso: {new Date(socio.socioInfo?.fechaIngreso).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>{socio.cedula}</td>
                      <td>
                        <div>
                          <div>{socio.email}</div>
                          <small className="text-muted">{socio.phone}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg="info">{socio.socioInfo?.numeroSocio}</Badge>
                      </td>
                      <td>
                        <Badge bg={socio.isActive ? 'success' : 'danger'}>
                          {socio.isActive ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {hasPermission('socios', 'update') && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => handleEditSocio(socio)}
                                title="Editar datos del socio"
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="outline-info"
                                onClick={() => handleChangePassword(socio)}
                                title="Cambiar contraseña"
                              >
                                <i className="fas fa-key"></i>
                              </Button>
                              
                              <Button
                                size="sm"
                                variant={socio.isActive ? 'outline-warning' : 'outline-success'}
                                onClick={() => toggleSocioStatus(socio._id, socio.isActive)}
                                title={socio.isActive ? 'Desactivar' : 'Activar'}
                              >
                                <i className={`fas fa-${socio.isActive ? 'pause' : 'play'}`}></i>
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleLoginAsSocio(socio)}
                                title="Iniciar sesión como este socio"
                              >
                                <i className="fas fa-sign-in-alt"></i>
                              </Button>
                            </>
                          )}
                          {hasPermission('socios', 'delete') && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteSocio(socio)}
                              title="Eliminar socio"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear nuevo socio */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
          <Modal.Title style={{ color: '#ffffff' }}>
            <i className="fas fa-user-plus me-2"></i>
            Nuevo Socio
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cédula *</Form.Label>
                  <Form.Control
                    type="text"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleInputChange}
                    required
                    placeholder="Número de cédula"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Nombre completo del socio"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Correo Electrónico *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="correo@ejemplo.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Celular</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+57 300 123 4567"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Dirección completa"
              />
            </Form.Group>
            
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Nota:</strong> La cédula será utilizada como contraseña inicial. 
              El socio podrá cambiarla después del primer ingreso.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              <i className="fas fa-save me-1"></i>
              Crear Socio
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para gestionar valores del socio */}
      <Modal show={showManageModal} onHide={() => setShowManageModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
          <Modal.Title style={{ color: '#ffffff' }}>
            <i className="fas fa-coins me-2"></i>
            Gestionar Valores - {selectedSocio?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateSocioData}>
          <Modal.Body>
            <Alert variant="info">
              <strong>Socio:</strong> {selectedSocio?.name}<br/>
              <strong>Número:</strong> {selectedSocio?.socioInfo?.numeroSocio}
            </Alert>
            
            <Form.Group className="mb-3">
              <Form.Label>Aportaciones ($)</Form.Label>
              <Form.Control
                type="number"
                name="aportaciones"
                value={manageData.aportaciones}
                onChange={handleManageInputChange}
                min="0"
                step="0.01"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Ahorros ($)</Form.Label>
              <Form.Control
                type="number"
                name="ahorros"
                value={manageData.ahorros}
                onChange={handleManageInputChange}
                min="0"
                step="0.01"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Préstamos ($)</Form.Label>
              <Form.Control
                type="number"
                name="prestamos"
                value={manageData.prestamos}
                onChange={handleManageInputChange}
                min="0"
                step="0.01"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowManageModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              <i className="fas fa-save me-1"></i>
              Actualizar Valores
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para editar datos del socio */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
          <Modal.Title style={{ color: '#ffffff' }}>
            <i className="fas fa-edit me-2"></i>
            Editar Socio - {selectedSocio?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateSocio}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cédula *</Form.Label>
                  <Form.Control
                    type="text"
                    name="cedula"
                    value={editData.cedula}
                    onChange={handleEditInputChange}
                    required
                    placeholder="Número de cédula"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={editData.name}
                    onChange={handleEditInputChange}
                    required
                    placeholder="Nombre completo del socio"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Correo Electrónico *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleEditInputChange}
                    required
                    placeholder="correo@ejemplo.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Celular</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={editData.phone}
                    onChange={handleEditInputChange}
                    placeholder="+57 300 123 4567"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                value={editData.address}
                onChange={handleEditInputChange}
                placeholder="Dirección completa"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button variant="success" type="submit">
              <i className="fas fa-save me-1"></i>
              Actualizar Datos
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para cambiar contraseña */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
          <Modal.Title style={{ color: '#ffffff' }}>
            <i className="fas fa-key me-2"></i>
            Cambiar Contraseña - {selectedSocio?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdatePassword}>
          <Modal.Body>
            <Alert variant="info">
              <strong>Socio:</strong> {selectedSocio?.name}<br/>
              <strong>Cédula:</strong> {selectedSocio?.cedula}
            </Alert>
            
            <Form.Group className="mb-3">
              <Form.Label>Nueva Contraseña *</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordInputChange}
                required
                minLength={6}
                placeholder="Mínimo 6 caracteres"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Confirmar Contraseña *</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordInputChange}
                required
                minLength={6}
                placeholder="Confirmar nueva contraseña"
              />
            </Form.Group>
            
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Importante:</strong> El socio deberá usar esta nueva contraseña para ingresar al sistema.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancelar
            </Button>
            <Button variant="info" type="submit">
              <i className="fas fa-key me-1"></i>
              Cambiar Contraseña
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal confirmar eliminación */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
          <Modal.Title style={{ color: '#ffffff' }}>
            <i className="fas fa-trash me-2"></i>
            Confirmar eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            ¿Estás seguro que deseas eliminar el socio <strong>{selectedSocio?.name}</strong> (Cédula: {selectedSocio?.cedula})? Esta acción no podrá deshacerse.
          </p>
          <p className="text-danger small">Nota: El sistema impedirá eliminar super-admins y tu propia cuenta.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDeleteSocio} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              'Eliminar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para ver socios activos */}
      <Modal show={showActiveSociosModal} onHide={() => setShowActiveSociosModal(false)} size="xl">
        <Modal.Header closeButton style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
          <Modal.Title style={{ color: '#ffffff' }}>
            <i className="fas fa-users me-2"></i>
            Socios Activos ({socios.filter(s => s.isActive).length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Socio</th>
                <th>Cédula</th>
                <th>Contacto</th>
                <th>Número Socio</th>
                <th>Aportaciones</th>
                <th>Ahorros</th>
                <th>Préstamos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {socios.filter(socio => socio.isActive).map(socio => (
                <tr key={socio._id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-user-circle fa-2x text-success me-2"></i>
                      <div>
                        <div className="fw-bold">{socio.name}</div>
                        <small className="text-muted">
                          Ingreso: {new Date(socio.socioInfo?.fechaIngreso).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>{socio.cedula}</td>
                  <td>
                    <div>
                      <div>{socio.email}</div>
                      <small className="text-muted">{socio.phone}</small>
                    </div>
                  </td>
                  <td>
                    <Badge bg="info">{socio.socioInfo?.numeroSocio}</Badge>
                  </td>
                  <td>
                    <span className="fw-bold text-success">
                      ${(socio.socioInfo?.aportaciones || 0).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span className="fw-bold text-info">
                      ${(socio.socioInfo?.ahorros || 0).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span className="fw-bold text-warning">
                      ${(socio.socioInfo?.prestamos || 0).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      {hasPermission('socios', 'update') && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => {
                              setShowActiveSociosModal(false);
                              handleEditSocio(socio);
                            }}
                            title="Editar datos del socio"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              setShowActiveSociosModal(false);
                              handleManageSocio(socio);
                            }}
                            title="Gestionar valores"
                          >
                            <i className="fas fa-coins"></i>
                          </Button>
                        </>
                      )}
                          {hasPermission('socios', 'delete') && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => {
                                setShowActiveSociosModal(false);
                                handleDeleteSocio(socio);
                              }}
                              title="Eliminar socio"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {socios.filter(s => s.isActive).length === 0 && (
            <Alert variant="info" className="text-center">
              <i className="fas fa-info-circle me-2"></i>
              No hay socios activos en el sistema.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActiveSociosModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SociosManagement;